<template>
  <div class="page">
    <header class="header">
      <el-button text class="back-btn" @click="handleBack" aria-label="返回">
        <img src="@/assets/返回.svg" alt="返回" class="back-icon" />
      </el-button>
      <h1 class="title">多物料多托盘归托</h1>
      <div class="header-right"></div>
    </header>

    <main class="content">
      <!-- 入库单信息 -->
      <div class="info-card">
        <div class="info-row">
          <span class="info-label">入库单号</span>
          <span class="info-value truncate">{{ detail.wareInNo || "-" }}</span>
        </div>
        <div class="info-row">
          <span class="info-label">供应商</span>
          <span class="info-value">{{ detail.supplierName || "-" }}</span>
        </div>
      </div>

      <!-- 物料选择区域 -->
      <div class="section">
        <div class="section-header">
          <div class="section-title">选择物料</div>
          <el-tag type="primary" size="small" round
            >已选 {{ selectedMaterialIds.length }} 种</el-tag
          >
        </div>
        <div class="material-table-container">
          <el-table
            :data="materialList"
            size="small"
            stripe
            row-class-name="material-row"
            :row-style="
              ({ row }) =>
                getRemainingQty(row.materialId) <= 0 ? { opacity: 0.5 } : {}
            "
            @row-click="(row) => toggleMaterialSelect(row.materialId)"
            style="width: 100%"
          >
            <el-table-column width="40" align="center">
              <template #default="{ row }">
                <el-checkbox
                  :model-value="isMaterialSelected(row.materialId)"
                  :disabled="getRemainingQty(row.materialId) <= 0"
                  @change="toggleMaterialSelect(row.materialId)"
                  @click.stop
                />
              </template>
            </el-table-column>
            <el-table-column prop="materialNo" label="物料编码" min-width="120">
              <template #default="{ row }">
                <span class="material-code">{{ row.materialNo }}</span>
              </template>
            </el-table-column>
            <el-table-column label="入库" width="70" align="center">
              <template #default="{ row }">
                {{ formatNum(row.wareInNum) }}
              </template>
            </el-table-column>
            <el-table-column label="剩余" width="70" align="right">
              <template #default="{ row }">
                <span
                  :class="{
                    'text-danger': getRemainingQty(row.materialId) > 0,
                  }"
                >
                  {{ formatNum(getRemainingQty(row.materialId)) }}
                </span>
              </template>
            </el-table-column>
          </el-table>
        </div>

        <!-- 已归托记录 -->
        <div v-if="collectedRecords.length" class="collected-records">
          <div class="section-header">
            <div class="section-title">已归托记录</div>
            <el-tag type="success" size="small" round
              >{{ collectedRecords.length }} 条</el-tag
            >
          </div>
          <el-table
            :data="collectedRecords"
            size="small"
            stripe
            style="width: 100%"
          >
            <el-table-column prop="palletCode" label="托盘号" min-width="100" />
            <el-table-column
              prop="materialNo"
              label="物料编码"
              min-width="100"
            />
            <el-table-column
              prop="wareInNum"
              label="归托数量"
              width="80"
              align="right"
            >
              <template #default="{ row }">
                {{ formatNum(row.wareInNum) }}
              </template>
            </el-table-column>
          </el-table>
        </div>
      </div>

      <!-- 托盘输入区域 -->
      <div class="section">
        <div class="section-header">
          <div class="section-title">托盘信息</div>
          <el-tag size="small" round>{{ palletList.length }} 个托盘</el-tag>
        </div>

        <div class="pallet-input-row">
          <el-input
            v-model="palletCode"
            class="pallet-input"
            placeholder="扫描/输入托盘码"
            @keyup.enter="addPallet"
            clearable
          >
            <template #suffix>
              <el-button
                type="text"
                class="scan-btn"
                @click="startScan"
                aria-label="扫码"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  width="20"
                  height="20"
                >
                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
                  <path
                    d="M7 7h3v3H7zM14 7h3v3h-3zM7 14h3v3H7zM14 14h3v3h-3z"
                  />
                </svg>
              </el-button>
            </template>
          </el-input>
          <el-button
            type="primary"
            class="add-pallet-btn"
            :disabled="!palletCode.trim() || !selectedMaterialIds.length"
            @click="addPallet"
          >
            添加托盘
          </el-button>
        </div>

        <!-- 已添加的托盘列表 -->
        <div v-if="palletList.length" class="pallet-list">
          <div
            v-for="(pallet, palletIndex) in palletList"
            :key="palletIndex"
            class="pallet-card"
          >
            <div class="pallet-header">
              <div class="pallet-info">
                <span class="pallet-code">📦 {{ pallet.palletCode }}</span>
              </div>
              <el-button
                type="danger"
                text
                size="small"
                @click="removePallet(palletIndex)"
              >
                删除
              </el-button>
            </div>
            <div class="pallet-materials">
              <el-table :data="pallet.items" size="small" style="width: 100%">
                <el-table-column
                  prop="materialNo"
                  label="物料编码"
                  min-width="100"
                />
                <el-table-column label="归托数量" width="80" align="center">
                  <template #default="{ row }">
                    {{ formatNum(row.wareInNum) }}
                  </template>
                </el-table-column>
                <el-table-column label="剩余" width="60" align="right">
                  <template #default="{ row }">
                    {{ formatNum(getRemainingQty(row.materialId)) }}
                  </template>
                </el-table-column>
              </el-table>
            </div>
          </div>
        </div>
        <el-empty v-else description="请先选择物料" :image-size="60" />
      </div>

      <!-- 统计信息 -->
      <div class="summary-card">
        <div class="summary-row">
          <div class="summary-item">
            <span class="summary-value">{{ selectedMaterialIds.length }}</span>
            <span class="summary-label">已选物料</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item">
            <span class="summary-value">{{ palletList.length }}</span>
            <span class="summary-label">已添加托盘</span>
          </div>
          <div class="summary-divider"></div>
          <div class="summary-item highlight">
            <span class="summary-value">{{
              formatNum(totalCollectedQty)
            }}</span>
            <span class="summary-label">已归托数量</span>
          </div>
        </div>
      </div>
    </main>

    <!-- 底部操作栏 -->
    <footer class="footer">
      <el-button
        type="primary"
        round
        class="confirm-btn"
        :disabled="!canSubmit || submitting"
        :loading="submitting"
        @click="handleSubmit"
      >
        {{ submitting ? "提交中..." : "确认收货" }}
      </el-button>
    </footer>

    <!-- 确认弹窗 -->
    <el-dialog
      v-model="confirmVisible"
      title="确认收货"
      width="85%"
      class="confirm-dialog"
    >
      <div class="confirm-msg">{{ confirmMessage }}</div>
      <template #footer>
        <el-button @click="confirmVisible = false" :disabled="submitting" round
          >取消</el-button
        >
        <el-button
          @click="submitCollect"
          :disabled="submitting"
          type="primary"
          round
          :loading="submitting"
        >
          确认
        </el-button>
      </template>
    </el-dialog>

    <!-- 扫码组件 -->
    <CameraScan
      v-model:visible="scanVisible"
      title="扫描托盘码"
      @success="handleScanSuccess"
      @close="scanVisible = false"
    />
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from "vue";
import { useRoute, useRouter } from "vue-router";
import { getWareInReceiveDetail, collectProMulti } from "@/api/wareInReceive";
import toast from "@/utils/toast";
import CameraScan from "@/components/CameraScan";

const route = useRoute();
const router = useRouter();

// 状态
const loading = ref(false);
const errorMsg = ref("");
const detail = ref({});
const materialList = ref([]);
const selectedMaterialIds = ref([]);
const palletCode = ref("");
const palletList = ref([]);
const scanVisible = ref(false);
const submitting = ref(false);
const confirmVisible = ref(false);
const confirmMessage = ref("");

// 计算属性
const totalCollectedQty = computed(() => {
  let total = 0;
  // 计算当前编辑的托盘列表中的归托数量
  palletList.value.forEach((pallet) => {
    pallet.items.forEach((item) => {
      total += Number(item.wareInNum) || 0;
    });
  });
  // 加上已有的归托记录中的数量
  collectedRecords.value.forEach((record) => {
    total += Number(record.wareInNum) || 0;
  });
  return total;
});

const canSubmit = computed(() => {
  return palletList.value.length > 0 || collectedRecords.value.length > 0;
});

const collectedRecords = computed(() => {
  const records = [];
  materialList.value.forEach((material) => {
    if (
      material.wareInDetailRecordVOList &&
      material.wareInDetailRecordVOList.length > 0
    ) {
      material.wareInDetailRecordVOList.forEach((record) => {
        if (record.wareInNum > 0) {
          records.push({
            palletCode: record.palletCode,
            materialNo: material.materialNo,
            materialDesc: material.materialDesc,
            wareInNum: record.wareInNum,
            wareName: record.wareName,
          });
        }
      });
    }
  });
  return records;
});

// 方法
const formatNum = (val) => {
  if (val === undefined || val === null || val === "") return "-";
  const num = Number(val);
  if (Number.isNaN(num)) return String(val);
  if (Number.isInteger(num)) {
    return String(num);
  }
  return num.toFixed(3).replace(/\.?0+$/, "");
};

const isMaterialSelected = (materialId) => {
  return selectedMaterialIds.value.includes(materialId);
};

const toggleMaterialSelect = (materialId) => {
  // 如果剩余数量为0，禁止选择
  if (getRemainingQty(materialId) <= 0) {
    return;
  }

  const index = selectedMaterialIds.value.indexOf(materialId);
  if (index > -1) {
    // 取消选择，但保留已添加到托盘中的物料
    selectedMaterialIds.value.splice(index, 1);
  } else {
    selectedMaterialIds.value.push(materialId);
  }
};

const getMaterialById = (materialId) => {
  return materialList.value.find((m) => m.materialId === materialId);
};

const getRemainingQty = (materialId) => {
  const material = getMaterialById(materialId);
  if (!material) return 0;

  const totalWareIn = Number(material.wareInNum) || 0;
  let collected = 0;

  // 计算当前编辑的托盘列表中的归托数量
  palletList.value.forEach((pallet) => {
    pallet.items.forEach((item) => {
      if (item.materialId === materialId) {
        collected += Number(item.wareInNum) || 0;
      }
    });
  });

  // 加上已有的归托记录中的数量
  collectedRecords.value.forEach((record) => {
    if (record.materialNo === material.materialNo) {
      collected += Number(record.wareInNum) || 0;
    }
  });

  return totalWareIn - collected;
};

const startScan = () => {
  scanVisible.value = true;
};

const handleScanSuccess = (decodedText) => {
  const text = String(decodedText || "")
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    .replace(/[\r\n\t]/g, "")
    .trim();

  if (!text) return;

  palletCode.value = text;
  scanVisible.value = false;
};

const addPallet = () => {
  const code = palletCode.value.trim();
  if (!code) return;
  if (selectedMaterialIds.value.length === 0) {
    errorMsg.value = "请先选择物料";
    setTimeout(() => (errorMsg.value = ""), 2000);
    return;
  }

  // 检查是否已存在相同托盘码
  const existingIndex = palletList.value.findIndex(
    (p) => p.palletCode === code,
  );
  if (existingIndex > -1) {
    errorMsg.value = "该托盘已添加";
    setTimeout(() => (errorMsg.value = ""), 2000);
    return;
  }

  // 检查是否所有选中物料的剩余数量都是0
  const allZeroRemaining = selectedMaterialIds.value.every(
    (materialId) => getRemainingQty(materialId) <= 0,
  );
  if (allZeroRemaining) {
    errorMsg.value = "所有选中物料的归托数量均为0，无法添加托盘";
    setTimeout(() => (errorMsg.value = ""), 2000);
    return;
  }

  // 创建新托盘，只包含剩余数量大于0的物料
  const items = selectedMaterialIds.value
    .filter((materialId) => getRemainingQty(materialId) > 0)
    .map((materialId) => {
      const material = getMaterialById(materialId);
      const remaining = getRemainingQty(materialId);
      return {
        materialId,
        materialNo: material?.materialNo || "",
        materialDesc: material?.materialDesc || "",
        wareInNum: remaining,
      };
    });

  palletList.value.push({
    palletCode: code,
    items,
  });

  palletCode.value = "";
};

const removePallet = (index) => {
  palletList.value.splice(index, 1);
};

const handleBack = () => {
  let url = "/warein-receive?status=pending";
  const supplierId = route.query?.supplierId;
  const supplierName = route.query?.supplierName;
  if (supplierId) url += `&supplierId=${supplierId}`;
  if (supplierName) url += `&supplierName=${encodeURIComponent(supplierName)}`;
  const nk = route.query?.noKeyword;
  const ns = route.query?.noSearchType;
  if (nk) url += `&noKeyword=${nk}`;
  if (ns) url += `&noSearchType=${ns}`;
  router.push(url);
};

const loadDetail = async () => {
  const wareInId = route.query.wareInId;
  if (!wareInId) {
    errorMsg.value = "入库单ID不存在";
    return;
  }

  loading.value = true;
  try {
    const res = await getWareInReceiveDetail(wareInId);
    detail.value = res?.data || {};
    materialList.value = Array.isArray(detail.value.wareInDetailList)
      ? detail.value.wareInDetailList
      : [];

    // 初始化时，只选中剩余数量大于0的物料
    selectedMaterialIds.value = materialList.value
      .filter((m) => Number(m.wareInNum) > 0)
      .map((m) => m.materialId);
  } catch (e) {
    errorMsg.value = `加载失败: ${e?.message || "网络异常"}`;
  } finally {
    loading.value = false;
  }
};

const handleSubmit = () => {
  const totalQty = materialList.value.reduce(
    (sum, m) => sum + (Number(m.wareInNum) || 0),
    0,
  );
  const remaining = totalQty - totalCollectedQty.value;

  if (remaining > 0) {
    confirmMessage.value = `已归托 ${formatNum(
      totalCollectedQty.value,
    )} 个，剩余 ${formatNum(remaining)} 个未归托，是否确认收货？`;
  } else {
    confirmMessage.value = "已全部归托完成，是否确认收货？";
  }
  confirmVisible.value = true;
};

const submitCollect = async () => {
  const wareInId = route.query.wareInId;
  if (!wareInId) return;

  // 检查是否有有效的归托数据（归托数量大于0）
  const hasValidCollect =
    palletList.value.some((pallet) =>
      pallet.items.some((item) => item.wareInNum > 0),
    ) || collectedRecords.value.length > 0;

  if (!hasValidCollect) {
    confirmVisible.value = false;
    errorMsg.value = "没有有效的归托数据，请至少设置一个大于0的归托数量";
    setTimeout(() => (errorMsg.value = ""), 2000);
    return;
  }

  confirmVisible.value = false;
  submitting.value = true;

  try {
    // 构建请求参数，只包含归托数量大于0的物料
    const wareInDetailRecordVOList = [];

    // 添加当前编辑的托盘数据
    palletList.value.forEach((pallet) => {
      pallet.items.forEach((item) => {
        if (item.wareInNum > 0) {
          wareInDetailRecordVOList.push({
            materialId: String(item.materialId),
            palletCode: pallet.palletCode,
            wareInNum: item.wareInNum,
          });
        }
      });
    });

    // 如果当前没有编辑的托盘，但有已有的归托记录，也提交已有的记录
    if (
      wareInDetailRecordVOList.length === 0 &&
      collectedRecords.value.length > 0
    ) {
      collectedRecords.value.forEach((record) => {
        const material = materialList.value.find(
          (m) => m.materialNo === record.materialNo,
        );
        if (material) {
          wareInDetailRecordVOList.push({
            materialId: String(material.materialId),
            palletCode: record.palletCode,
            wareInNum: record.wareInNum,
          });
        }
      });
    }

    const params = {
      wareInId: String(wareInId),
      type: 0,
      wareInDetailRecordVOList,
    };

    await collectProMulti(params);

    toast.success("收货成功");
    setTimeout(() => {
      let url = "/warein-receive?status=pending";
      const supplierId = route.query?.supplierId;
      const supplierName = route.query?.supplierName;
      if (supplierId) url += `&supplierId=${supplierId}`;
      if (supplierName)
        url += `&supplierName=${encodeURIComponent(supplierName)}`;
      const nk = route.query?.noKeyword;
      const ns = route.query?.noSearchType;
      if (nk) url += `&noKeyword=${nk}`;
      if (ns) url += `&noSearchType=${ns}`;
      router.push(url);
    }, 1500);
  } catch (e) {
    toast.error(e?.message || "收货失败");
  } finally {
    submitting.value = false;
  }
};

onMounted(() => {
  loadDetail();
});
</script>

<style scoped>
.page {
  min-height: 100vh;
  background-color: var(--background-color);
  padding-bottom: 160px;
}

.page,
.page * {
  box-sizing: border-box;
}

.header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 12px;
  padding-top: calc(8px + env(safe-area-inset-top));
  background-color: var(--header-background);
  color: var(--header-text);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  height: calc(52px + env(safe-area-inset-top));
  box-shadow: 0 2px 8px var(--shadow-light);
}

.back-btn {
  width: 36px;
  height: 36px;
}

.back-icon {
  width: 20px;
  height: 20px;
}

.title {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
}

.header-right {
  width: 36px;
}

.content {
  padding: 8px 10px;
  padding-top: calc(52px + env(safe-area-inset-top));
}

.info-card {
  background-color: var(--card-background);
  border-radius: 10px;
  padding: 8px 12px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 6px 0;
  border-bottom: 1px solid var(--border-color);
}

.info-row:last-child {
  border-bottom: none;
}

.info-label {
  font-size: 13px;
  color: var(--text-secondary);
}

.info-value {
  font-size: 13px;
  color: var(--text-primary);
  font-weight: 600;
}

.info-value.truncate {
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.section {
  background-color: var(--card-background);
  border-radius: 10px;
  padding: 10px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.material-table-container {
  max-height: 260px;
  overflow-y: auto;
  border-radius: 8px;
}

.text-danger {
  color: #ef4444;
  font-weight: 600;
}

.material-code {
  font-size: 13px;
  font-weight: 600;
  color: var(--text-primary);
}

.collected-records {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--border-color);
  max-height: 300px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.pallet-input-row {
  display: flex;
  gap: 8px;
  margin-bottom: 10px;
}

.pallet-input {
  flex: 1;
}

.pallet-input :deep(.el-input__wrapper) {
  height: 40px;
  border-radius: 8px;
}

.pallet-input :deep(.el-input__suffix) {
  pointer-events: auto;
}

.pallet-input .scan-btn {
  padding: 0;
  margin-right: 4px;
  color: #5bc0de;
}

.pallet-input .scan-btn:hover {
  color: #5bc0de;
}

.add-pallet-btn {
  height: 40px;
  padding: 0 16px;
  white-space: nowrap;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.pallet-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 320px;
  overflow-y: auto;
}

.pallet-card {
  background-color: var(--card-background);
  border-radius: 8px;
  border: 1px solid var(--border-color);
}

.pallet-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 10px;
  background: rgba(35, 177, 192, 0.06);
  border-bottom: 1px solid var(--border-color);
}

.pallet-info {
  display: flex;
  align-items: center;
  gap: 6px;
}

.pallet-code {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.pallet-materials {
  padding: 6px;
  max-height: 200px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

.summary-card {
  background: var(--card-background);
  border-radius: 10px;
  padding: 12px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px var(--shadow-light);
}

.summary-row {
  display: flex;
  justify-content: space-around;
  align-items: center;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.summary-value {
  font-size: 20px;
  font-weight: 700;
  color: var(--text-primary);
}

.summary-label {
  font-size: 11px;
  color: var(--text-secondary);
  margin-top: 2px;
}

.summary-item.highlight .summary-value {
  font-size: 22px;
  color: var(--primary-color);
}

.summary-divider {
  width: 1px;
  height: 32px;
  background-color: var(--border-color);
}

.footer {
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 10px 12px;
  padding-bottom: calc(10px + env(safe-area-inset-bottom));
  background-color: var(--card-background);
  box-shadow: 0 -2px 8px var(--shadow-light);
}

.footer .confirm-btn {
  width: 100%;
  height: 44px;
  --el-button-bg-color: #5bc0de !important;
  --el-button-border-color: #5bc0de !important;
}

.confirm-msg {
  font-size: 14px;
  color: var(--text-primary);
  line-height: 1.6;
  text-align: center;
  padding: 8px 0;
}

.confirm-msg.error-msg {
  color: #ef4444;
}

.confirm-msg.success-msg {
  color: #10b981;
}

.confirm-dialog :deep(.el-dialog) {
  border-radius: 12px;
}
</style>
